// Die rechte Spalte der Freigabe: das Material (E3, S3; Mockup
// /crm/freigabe/:id, „Material für diese Anlage“). Drei Gruppen –
// „Standard – immer dabei“, „Aus der Aufnahme“ (von selbst), „Vom Büro
// ergänzt“ (aus der Auswahl oder frei) –, die Mengen änderbar, Positionen
// entfernbar. Unten groß „Freigeben“ (bis E3c „Freigeben – Liste ans
// Lager“, in S1 kurz „Material freigeben“ – Gernot: es bleibt „Freigeben“;
// seit E3c geht die Materialliste mit der Unterlage an die Bodenpartie, H26).
//
// Vor der Freigabe lebt der Entwurf im Browser (material.ts): Erst wenn das
// Büro etwas ändert, wird er gespeichert – bis dahin zeigt die Spalte immer
// die frische Vorbelegung. Zwei Fenster teilen ihn und gleichen sich live
// ab (speicher.ts). Freigeben geht nur einmal – deshalb vorher eine kurze
// Rückfrage.
//
// Nach der Freigabe: die Liste, wie sie gilt, mit „Material
// ändern“ – dieselben Gruppen zum Bearbeiten, „Änderung speichern“. Der
// Server ersetzt, was sich geändert hat, und vergibt dafür neue Kennungen –
// darum liest die Seite danach immer neu.
//
// Positionen aus der Aufnahme lassen sich – anders als im Plan nur „Menge
// änderbar“ – auch entfernen: Entscheidet das Büro, dass ein Rohr nicht
// gebraucht wird, soll auf der Materialliste keine Zeile „0 m Rohr“ stehen.
// „Entwurf verwerfen“ holt sie zurück.
//
// Seit E3b (O65): „Aus der Aufnahme“ bringt auch das Material, das der
// Verkäufer draußen angegeben hat – je Zeile mit der Station („aus: Station
// 2 · …“) oder „Material am Schluss“. Das Büro entscheidet wie bisher
// (Grundsatz 7). Ändert ein Nachtrag Längen oder Material, bietet die
// Spalte „Aus der Aufnahme neu übernehmen“ an.

import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import {
  darf,
  standardOrdnen,
  type CrmAufnahme,
  type FreigebenAntwort,
  type MaterialAntwort,
  type MaterialEingabe,
  type StandardlisteAntwort,
  type StandardmaterialZeile,
} from "@happy-energy/kern";
import { ApiFehler, Chip, Ikon, senden, Spaeter, Tipp, toastZeigen } from "@happy-energy/oberflaeche";
import { wannZeigen, zeitpunktZeigen } from "../anzeige.ts";
import { Nachfrage } from "../bausteine.tsx";
import { fehlerText, Laedt, LadeFehler, useLaden } from "../laden.tsx";
import {
  aenderungAus,
  aenderungSchluessel,
  aufnahmeNeuUebernehmen,
  auswahlPosition,
  dazu,
  entfernen,
  entwurfLesen,
  entwurfSchluessel,
  freieEingabePruefen,
  freiePosition,
  geltendeGrundlage,
  geordnet,
  GRUPPEN,
  grundlageListe,
  aufnahmeFingerabdruck,
  mengeLesen,
  mengeSetzen,
  mengeZeigen,
  mitMengeNull,
  vorbelegung,
  wieInDerAufnahme,
  woherText,
  zumSenden,
  type Entwurf,
  type EntwurfPosition,
  type Mangel,
} from "../material.ts";
import { anderenFensternMelden, gespeichertSchreiben, useGespeichert } from "../speicher.ts";
import { useCrm } from "../zusammenhang.tsx";

type Bearbeiten = (aenderung: (e: Entwurf) => Entwurf) => void;

/** Der Rahmen der Spalte: dunkler Kopf, die Liste scrollt, unten der Fuß mit dem großen Knopf. */
function MaterialKarte({ chip, children, fuss }: { chip: ReactNode; children: ReactNode; fuss?: ReactNode }) {
  return (
    <section className="material-karte" aria-label="Material für diese Anlage">
      <div className="mat-kopf">
        <Ikon name="paket" />
        <h3>Material für diese Anlage</h3>
        {chip}
      </div>
      <div className="mat-scroll">{children}</div>
      {fuss && <div className="mat-fuss">{fuss}</div>}
    </section>
  );
}

function PositionZeile({
  p,
  her,
  bearbeiten,
  mangel,
  fokus,
}: {
  p: EntwurfPosition;
  her: string | null;
  bearbeiten: Bearbeiten | null;
  mangel: string | undefined;
  fokus: boolean;
}) {
  const ungueltig = mengeLesen(p.menge) === null;
  return (
    <div className={`mat-zeile${mangel ? " mit-mangel" : ""}`}>
      {bearbeiten ? (
        <input
          className={`mat-menge${ungueltig ? " ungueltig" : mengeLesen(p.menge) === 0 ? " null" : ""}`}
          inputMode="decimal"
          value={p.menge}
          aria-label={`Menge für ${p.bezeichnung}`}
          aria-invalid={ungueltig || undefined}
          // Nur eine eben angefügte Position bekommt den Fokus – man tippt gleich die Menge.
          autoFocus={fokus}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => {
            const text = e.target.value;
            bearbeiten((x) => mengeSetzen(x, p.id, text));
          }}
        />
      ) : (
        <b className="mat-menge-lesen">{p.menge}</b>
      )}
      <span className="mat-einheit">{p.einheit}</span>
      <div className="mat-wort">
        {p.bezeichnung}
        {her && <span className="her">aus: {her}</span>}
        {p.artikelnummer && <span className="her">Art.-Nr. {p.artikelnummer}</span>}
      </div>
      {bearbeiten && (
        <button type="button" className="mat-weg" aria-label={`${p.bezeichnung} entfernen`} title="Entfernen" onClick={() => bearbeiten((x) => entfernen(x, p.id))}>
          <Ikon name="kreuz" groesse="k" />
        </button>
      )}
      {mangel && <span className="mat-mangel">{mangel}</span>}
    </div>
  );
}

/** „+ aus der Auswahl“ und „+ frei“ – unter „Vom Büro ergänzt“. */
function Ergaenzen({ auswahl, hinzu, darfListe }: { auswahl: readonly StandardmaterialZeile[] | null; hinzu: (p: EntwurfPosition) => void; darfListe: boolean }) {
  const [offen, setOffen] = useState<"auswahl" | "frei" | null>(null);
  const [frei, setFrei] = useState({ bezeichnung: "", menge: "1", einheit: "Stk" });
  const [meldung, setMeldung] = useState<string | null>(null);

  function freiDazu(e: FormEvent) {
    e.preventDefault();
    const fehler = freieEingabePruefen(frei);
    if (fehler) {
      setMeldung(fehler);
      return;
    }
    hinzu(freiePosition(frei));
    setFrei({ bezeichnung: "", menge: "1", einheit: frei.einheit });
    setMeldung(null);
    setOffen(null);
  }

  return (
    <div className="mat-ergaenzen">
      <div className="mat-ergaenzen-knoepfe">
        <button type="button" className={`crm-knopf rand klein${offen === "auswahl" ? " an" : ""}`} aria-expanded={offen === "auswahl"} onClick={() => setOffen(offen === "auswahl" ? null : "auswahl")}>
          <Ikon name="plus" groesse="k" /> aus der Auswahl
        </button>
        <button type="button" className={`crm-knopf rand klein${offen === "frei" ? " an" : ""}`} aria-expanded={offen === "frei"} onClick={() => setOffen(offen === "frei" ? null : "frei")}>
          <Ikon name="plus" groesse="k" /> frei
        </button>
      </div>
      {offen === "auswahl" && (
        <div className="mat-auswahl" role="group" aria-label="Aus der Auswahl ergänzen">
          {auswahl === null && <Laedt text="Auswahl wird geladen …" />}
          {auswahl?.length === 0 && (
            <p className="leise klein">
              Noch keine Auswahl in der Standardliste.{" "}
              {darfListe && (
                <a className="link" href="#/material">
                  Unter „Standardmaterial“ anlegen
                </a>
              )}
            </p>
          )}
          {/* In der Reihenfolge der Standardliste (seit dem Augentest E3b, Kern: standard-ordnung.ts). */}
          {auswahl && standardOrdnen(auswahl).map((e) => (
            <button type="button" key={e.id} className="mat-auswahl-knopf" onClick={() => hinzu(auswahlPosition(e))}>
              <Ikon name="plus" groesse="k" />
              <span>{e.bezeichnung}</span>
              <span className="mat-auswahl-menge">
                {mengeZeigen(e.menge)} {e.einheit}
              </span>
            </button>
          ))}
        </div>
      )}
      {offen === "frei" && (
        <form className="mat-frei" onSubmit={freiDazu} aria-label="Frei ergänzen">
          <label className="mat-feld mat-feld-breit">
            <span>Bezeichnung</span>
            <input value={frei.bezeichnung} onChange={(e) => setFrei({ ...frei, bezeichnung: e.target.value })} placeholder="z. B. Wanddurchführung" autoFocus />
          </label>
          <label className="mat-feld">
            <span>Menge</span>
            <input value={frei.menge} inputMode="decimal" onChange={(e) => setFrei({ ...frei, menge: e.target.value })} />
          </label>
          <label className="mat-feld">
            <span>Einheit</span>
            <input value={frei.einheit} onChange={(e) => setFrei({ ...frei, einheit: e.target.value })} />
          </label>
          {meldung && <span className="mat-mangel mat-feld-breit">{meldung}</span>}
          <div className="mat-frei-knoepfe mat-feld-breit">
            <button type="button" className="crm-knopf leise klein" onClick={() => setOffen(null)}>
              Abbrechen
            </button>
            <button type="submit" className="crm-knopf dunkel klein">
              <Ikon name="plus" groesse="k" /> Dazu
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/** Die drei Gruppen – zum Lesen (`bearbeiten` null) oder Bearbeiten. */
function MaterialListe({
  a,
  positionen,
  bearbeiten,
  maengel,
  fokusId,
  auswahl,
  hinzu,
}: {
  a: CrmAufnahme;
  positionen: readonly EntwurfPosition[];
  bearbeiten: Bearbeiten | null;
  maengel: readonly Mangel[];
  fokusId: string | null;
  auswahl: readonly StandardmaterialZeile[] | null;
  hinzu: (p: EntwurfPosition) => void;
}) {
  const { ich } = useCrm();
  const darfListe = darf(ich.nutzer.rolle, "standardliste");
  const liste = geordnet(positionen);
  return (
    <>
      {GRUPPEN.map((g) => {
        const hier = liste.filter((p) => p.herkunft === g.herkunft);
        return (
          <div key={g.herkunft} className="mat-gruppe">
            <h4>
              <span>{g.titel}</span>
              {g.herkunft === "standard" && darfListe && (
                <a className="mat-gruppe-link" href="#/material">
                  Liste pflegen
                </a>
              )}
              {g.herkunft === "aufnahme" && <Chip art="lime">von selbst</Chip>}
            </h4>
            {hier.map((p) => (
              <PositionZeile
                key={p.id}
                p={p}
                her={woherText(p, a.stationen)}
                bearbeiten={bearbeiten}
                mangel={maengel.find((m) => m.id === p.id)?.meldung}
                fokus={p.id === fokusId}
              />
            ))}
            {hier.length === 0 && (
              <p className="mat-leer">
                {g.herkunft === "standard"
                  ? "Keine Standard-Positionen."
                  : g.herkunft === "aufnahme"
                    ? "Nichts aus der Aufnahme – der Verkauf hat kein Material angegeben."
                    : "Noch nichts ergänzt."}
              </p>
            )}
            {g.herkunft === "standard" && bearbeiten && hier.length > 0 && <p className="mat-leer">Vorbelegt aus der Standardliste des Büros.</p>}
            {g.herkunft === "buero" && bearbeiten && <Ergaenzen auswahl={auswahl} hinzu={hinzu} darfListe={darfListe} />}
          </div>
        );
      })}
      <div className="mat-gruppe mat-gruppe-spaeter">
        <Spaeter titel="Module und Schienen aus der K2-Stückliste" wann="Stufe 2">
          Kommen dann von selbst aus dem K2-PDF dazu.
        </Spaeter>
      </div>
    </>
  );
}

/** Die Fehler, die nicht an einer Position hängen – und der Hinweis, wenn welche markiert sind. */
function MaengelHinweis({ maengel }: { maengel: readonly Mangel[] }) {
  if (maengel.length === 0) return null;
  const allgemein = maengel.filter((m) => m.id === null);
  return (
    <Tipp art="rot" ikon="achtung">
      {allgemein.length > 0 ? allgemein.map((m) => m.meldung).join(" ") : "Bitte erst die rot markierten Positionen berichtigen."}
    </Tipp>
  );
}

/**
 * Hält einen Entwurf: gespeichert im Browser, und falls der Speicher
 * streikt (privater Modus, voll), wenigstens für diese Seite im
 * Arbeitsspeicher – dann sagt ein Hinweis, dass er ein Neuladen nicht übersteht.
 */
function useEntwurf(schluessel: string, lesen: (roh: string | null) => Entwurf | null) {
  const roh = useGespeichert(schluessel);
  const gespeichert = useMemo(() => lesen(roh), [lesen, roh]);
  const [notfall, setNotfall] = useState<Entwurf | null>(null);
  const [maengel, setMaengel] = useState<Mangel[]>([]);
  const entwurf = gespeichert ?? notfall;

  function speichern(neu: Entwurf | null) {
    const ok = gespeichertSchreiben(schluessel, neu === null ? null : JSON.stringify(neu));
    if (ok || neu === null) {
      setNotfall(null);
    } else {
      setNotfall(neu);
      toastZeigen("Dieser Browser speichert den Entwurf nicht – bitte gleich fertig machen.", "fehler");
    }
    // Waren Fehler markiert, zeigt die Liste gleich, ob sie behoben sind.
    setMaengel((alt) => {
      if (neu === null || alt.length === 0) return [];
      const b = zumSenden(neu);
      return b.ok ? [] : b.maengel;
    });
  }
  return { entwurf, gespeichert: gespeichert !== null, speichern, maengel, setMaengel };
}

// --- Vor der Freigabe ------------------------------------------------------------------------------

function VorFreigabe({ a, neuLaden }: { a: CrmAufnahme; neuLaden: () => void }) {
  const standard = useLaden<StandardlisteAntwort>("/api/crm/standardmaterial");
  const schluessel = entwurfSchluessel(a.id);
  const lesen = useMemo(() => (roh: string | null) => entwurfLesen(roh, a.id, "freigabe"), [a.id]);
  const { entwurf: gespeichert, speichern, maengel, setMaengel } = useEntwurf(schluessel, lesen);
  const eintraege = standard.daten?.eintraege;
  const frisch = useMemo(() => (eintraege ? vorbelegung(a.id, eintraege, a) : null), [a, eintraege]);
  const entwurf = gespeichert ?? frisch;
  const auswahl = useMemo(() => (eintraege ? eintraege.filter((e) => e.aktiv && e.art === "auswahl") : null), [eintraege]);

  const [fokusId, setFokusId] = useState<string | null>(null);
  const [nachfrage, setNachfrage] = useState<"freigeben" | "verwerfen" | null>(null);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const bearbeiten: Bearbeiten | null = entwurf ? (f) => speichern(f(entwurf)) : null;
  const aufnahmeNeu = gespeichert !== null && gespeichert.grundlage !== aufnahmeFingerabdruck(a);

  function fragen() {
    if (!entwurf) return;
    const b = zumSenden(entwurf);
    if (!b.ok) {
      setMaengel(b.maengel);
      return;
    }
    setMaengel([]);
    setFehler(null);
    setNachfrage("freigeben");
  }

  async function freigeben() {
    if (!entwurf) return;
    const b = zumSenden(entwurf);
    if (!b.ok) {
      setMaengel(b.maengel);
      setNachfrage(null);
      return;
    }
    setSendet(true);
    try {
      const antwort = await senden<FreigebenAntwort>(`/api/crm/aufnahme/${a.id}/freigeben`, b.eingabe);
      // Den Entwurf räumt die Liste nach der Freigabe weg (NachFreigabe) – bis
      // dahin bleibt er stehen, so springt die Spalte nicht kurz auf die Vorbelegung.
      toastZeigen(
        antwort.ok && antwort.schonFreigegeben
          ? "Schon freigegeben (von jemand anderem) – es gilt jene Liste; ändern geht über „Material ändern“."
          : "Freigegeben – die Materialliste steht",
      );
      anderenFensternMelden(a.id);
      neuLaden();
    } catch (f) {
      setFehler(fehlerText(f));
    } finally {
      setSendet(false);
      setNachfrage(null);
    }
  }

  if (!entwurf) {
    return (
      <MaterialKarte chip={<Chip art="dunkel">Entwurf</Chip>}>
        <div className="mat-gruppe">
          {standard.fehler !== undefined ? <LadeFehler fehler={standard.fehler} nochmal={standard.neu} /> : <Laedt text="Standardliste wird geladen …" />}
        </div>
      </MaterialKarte>
    );
  }

  const nullen = mitMengeNull(entwurf);
  return (
    <MaterialKarte
      chip={<Chip art="dunkel">{gespeichert ? "Entwurf" : "Vorbelegung"}</Chip>}
      fuss={
        <>
          <MaengelHinweis maengel={maengel} />
          {fehler && (
            <Tipp art="rot" ikon="achtung">
              {fehler}
            </Tipp>
          )}
          {nullen > 0 && <p className="mat-hinweis">{nullen === 1 ? "Eine Position hat" : `${nullen} Positionen haben`} die Menge 0 – sie steht so auf der Materialliste.</p>}
          <button type="button" className="crm-knopf lime gross block" onClick={fragen} disabled={sendet || entwurf.positionen.length === 0}>
            <Ikon name="schild" /> Freigeben
          </button>
          <div className="mat-fuss-zeile">
            <span>{gespeichert ? "Entwurf gespeichert – nur in diesem Browser, bis du freigibst." : "Vorbelegt aus Standardliste und Aufnahme (Längen und Material von draußen)."}</span>
            {gespeichert && (
              <button type="button" className="crm-knopf leise klein" onClick={() => setNachfrage("verwerfen")}>
                Entwurf verwerfen
              </button>
            )}
          </div>
        </>
      }
    >
      {aufnahmeNeu && (
        <div className="mat-gruppe">
          <Tipp art="gelb" ikon="paket">
            Seit dem Entwurf hat sich geändert, was aus der Aufnahme kommt (Nachtrag).
            <div className="crm-tipp-knoepfe">
              <button type="button" className="crm-knopf rand klein" onClick={() => speichern(aufnahmeNeuUebernehmen(entwurf, a))}>
                Aus der Aufnahme neu übernehmen
              </button>
            </div>
          </Tipp>
        </div>
      )}
      <MaterialListe
        a={a}
        positionen={entwurf.positionen}
        bearbeiten={bearbeiten}
        maengel={maengel}
        fokusId={fokusId}
        auswahl={auswahl}
        hinzu={(p) => {
          setFokusId(p.id);
          speichern(dazu(entwurf, p));
        }}
      />
      {nachfrage === "freigeben" && (
        <Nachfrage titel="Freigeben?" jaText="Freigeben" ja={() => void freigeben()} nein={() => setNachfrage(null)} ikon="schild" beschaeftigt={sendet}>
          <p>
            Die Liste mit {entwurf.positionen.length === 1 ? "einer Position" : `${entwurf.positionen.length} Positionen`} wird die Materialliste dieser Anlage – sie
            kommt auf die Unterlage für die Montage. Freigeben geht nur einmal – danach änderst du das Material mit „Material ändern“.
          </p>
        </Nachfrage>
      )}
      {nachfrage === "verwerfen" && (
        <Nachfrage
          titel="Entwurf verwerfen?"
          jaText="Verwerfen"
          ja={() => {
            speichern(null);
            setNachfrage(null);
            toastZeigen("Entwurf verworfen – wieder die Vorbelegung");
          }}
          nein={() => setNachfrage(null)}
          ikon="zurueck"
          gefahr
        >
          <p>Deine Änderungen an Mengen und Positionen sind dann weg; die Liste beginnt wieder mit Standard und Aufnahme.</p>
        </Nachfrage>
      )}
    </MaterialKarte>
  );
}

// --- Nach der Freigabe -------------------------------------------------------------------------------

/** Wann das Material zuletzt nach der Freigabe geändert wurde – null: noch nie. */
function zuletztGeaendert(a: CrmAufnahme): string | null {
  if (a.freigegebenAm === null) return null;
  const frei = new Date(a.freigegebenAm).getTime();
  const zeiten = a.positionen.flatMap((p) => [p.ersetztAm, p.angelegtAm]).filter((z): z is string => z !== null && new Date(z).getTime() > frei);
  return zeiten.length === 0 ? null : zeiten.reduce((x, y) => (x > y ? x : y));
}

function NachFreigabe({ a, neuLaden }: { a: CrmAufnahme; neuLaden: () => void }) {
  const grundlage = useMemo(() => geltendeGrundlage(a.positionen), [a.positionen]);
  const schluessel = aenderungSchluessel(a.id);
  const lesen = useMemo(() => (roh: string | null) => entwurfLesen(roh, a.id, "aenderung", grundlage), [a.id, grundlage]);
  const { entwurf: aenderung, speichern, maengel, setMaengel } = useEntwurf(schluessel, lesen);
  const freigegeben = useMemo(() => aenderungAus(a.id, a.positionen), [a.id, a.positionen]);
  // Die Auswahl braucht es nur beim Ändern.
  const standard = useLaden<StandardlisteAntwort>(aenderung ? "/api/crm/standardmaterial" : null);
  const auswahl = useMemo(() => (standard.daten ? standard.daten.eintraege.filter((e) => e.aktiv && e.art === "auswahl") : null), [standard.daten]);

  const [fokusId, setFokusId] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const geaendert = zuletztGeaendert(a);

  // Der Entwurf von vor der Freigabe hat ausgedient – auch der eines zweiten Fensters.
  useEffect(() => {
    gespeichertSchreiben(entwurfSchluessel(a.id), null);
  }, [a.id]);

  async function speichernAmServer() {
    if (!aenderung) return;
    const b = zumSenden(aenderung);
    if (!b.ok) {
      setMaengel(b.maengel);
      return;
    }
    setSendet(true);
    setFehler(null);
    try {
      // Mit der Grundlage: Hat inzwischen jemand anderer geändert, weist der Server die Liste ab (409).
      const eingabe: MaterialEingabe = { ...b.eingabe, grundlage: grundlageListe(aenderung) };
      const antwort = await senden<MaterialAntwort>(`/api/crm/aufnahme/${a.id}/material`, eingabe);
      speichern(null);
      toastZeigen(antwort.ok && antwort.geaendert ? "Material geändert – die Unterlage zeigt die neue Liste" : "Nichts geändert");
      anderenFensternMelden(a.id);
      neuLaden();
    } catch (f) {
      if (f instanceof ApiFehler && f.status === 409) {
        // Die Liste passt nicht mehr zum Server (jemand anderer war schneller): den Entwurf weg, neu laden, sagen warum –
        // wie bei „Gesehen“ (Aufnahme.tsx). Vorher blieb der veraltete Entwurf mit einer Meldung stehen, die in die Irre führte
        // (Code-Prüfung E3, Befund 1).
        speichern(null);
        setFehler(`${fehlerText(f)} Die Liste ist jetzt neu geladen – bitte deine Änderung noch einmal machen.`);
        anderenFensternMelden(a.id);
        neuLaden();
      } else {
        setFehler(fehlerText(f));
      }
    } finally {
      setSendet(false);
    }
  }

  const info = (
    <p className="mat-info">
      <Ikon name="haken" groesse="k" /> Freigegeben {a.freigegebenAm ? zeitpunktZeigen(a.freigegebenAm) : ""}
      {a.freigegebenVon && ` · ${a.freigegebenVon.name}`}
      {geaendert && <span> · zuletzt geändert {wannZeigen(geaendert)}</span>}
    </p>
  );

  if (!aenderung) {
    return (
      <MaterialKarte
        chip={
          <Chip art="lime" ikon="haken">
            freigegeben
          </Chip>
        }
        fuss={
          <>
            {fehler && (
              <Tipp art="rot" ikon="achtung">
                {fehler}
              </Tipp>
            )}
            <button
              type="button"
              className="crm-knopf dunkel gross block"
              onClick={() => {
                setFehler(null);
                speichern(aenderungAus(a.id, a.positionen));
              }}
            >
              <Ikon name="stift" /> Material ändern
            </button>
            <p className="mat-hinweis">Die Unterlage für die Montage zeigt danach die neue Liste.</p>
          </>
        }
      >
        <div className="mat-gruppe">{info}</div>
        <MaterialListe a={a} positionen={freigegeben.positionen} bearbeiten={null} maengel={[]} fokusId={null} auswahl={null} hinzu={() => undefined} />
      </MaterialKarte>
    );
  }

  const bearbeiten: Bearbeiten = (f) => speichern(f(aenderung));
  const aufnahmeAnders = a.hinweis === "bitte-ansehen" && !wieInDerAufnahme(aenderung, a);
  return (
    <MaterialKarte
      chip={<Chip art="warn">wird geändert</Chip>}
      fuss={
        <>
          <MaengelHinweis maengel={maengel} />
          {fehler && (
            <Tipp art="rot" ikon="achtung">
              {fehler}
            </Tipp>
          )}
          <button type="button" className="crm-knopf lime gross block" onClick={() => void speichernAmServer()} disabled={sendet}>
            {sendet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="haken" />} Änderung speichern
          </button>
          <div className="mat-fuss-zeile">
            <span>Die Unterlage für die Montage zeigt danach die neue Liste.</span>
            <button type="button" className="crm-knopf leise klein" onClick={() => speichern(null)} disabled={sendet}>
              Abbrechen
            </button>
          </div>
        </>
      }
    >
      <div className="mat-gruppe">{info}</div>
      {aufnahmeAnders && (
        <div className="mat-gruppe">
          <Tipp art="gelb" ikon="paket">
            Was aus der Aufnahme kommt, ergibt jetzt andere Mengen als diese Liste (nach dem Nachtrag).
            <div className="crm-tipp-knoepfe">
              <button type="button" className="crm-knopf rand klein" onClick={() => speichern(aufnahmeNeuUebernehmen(aenderung, a))}>
                Aus der Aufnahme übernehmen
              </button>
            </div>
          </Tipp>
        </div>
      )}
      <MaterialListe
        a={a}
        positionen={aenderung.positionen}
        bearbeiten={bearbeiten}
        maengel={maengel}
        fokusId={fokusId}
        auswahl={auswahl}
        hinzu={(p) => {
          setFokusId(p.id);
          speichern(dazu(aenderung, p));
        }}
      />
    </MaterialKarte>
  );
}

/** Das Material einer Besichtigung – vor oder nach der Freigabe. */
export function Material({ a, neuLaden }: { a: CrmAufnahme; neuLaden: () => void }) {
  return a.stand === "freigegeben" ? <NachFreigabe a={a} neuLaden={neuLaden} /> : <VorFreigabe a={a} neuLaden={neuLaden} />;
}
